import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wallet, Sparkles, ArrowRight, Loader2, Download, MessageSquare } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import api, { LOCATIONS, PROJECT_TYPES, FINISH_LEVELS, BUILDING_TYPES, formatZARFull, trackEvent } from '@/lib/api';
import ReportLeadGate from '@/components/ReportLeadGate';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const CHART_COLORS = ['#CBA871', '#1A1A1A', '#52525B', '#A1A1AA'];

export default function CostCalculator() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ project_type: 'new_build', size_sqm: '', finish_level: 'standard', location: '', building_type: '', email: '', name: '', phone: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiExplanation, setAiExplanation] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [gateSubmitted, setGateSubmitted] = useState(false);

  useEffect(() => { trackEvent('page_view', { page: 'cost_calculator' }); }, []);

  const handleCalculate = async () => {
    if (!form.size_sqm || !form.location) { toast.error('Please fill in all required fields'); return; }
    // Top-of-funnel impression — visitor completed criteria and hit Calculate.
    api.post('/tools/impression', {
      tool: 'cost',
      criteria: {
        project_type: form.project_type, size_sqm: Number(form.size_sqm),
        finish_level: form.finish_level, location: form.location, building_type: form.building_type,
      },
    }).catch(() => {});
    if (gateSubmitted) { runCalculation(); return; }
    setShowGate(true);
  };

  const runCalculation = async (contact) => {
    setLoading(true); setResult(null); setAiExplanation('');
    try {
      const payload = { ...form, ...(contact || {}), size_sqm: Number(form.size_sqm) };
      if (contact) setForm((f) => ({ ...f, ...contact }));
      const { data } = await api.post('/cost/calculate', payload);
      setResult(data);
      setGateSubmitted(true);
      setShowGate(false);
      trackEvent('cost_calculated', { finish_level: form.finish_level, location: form.location });
      toast.success('Cost breakdown generated');
      fetchAiExplanation(data);
    } catch (e) {
      toast.error('Calculation failed');
      throw e;
    } finally { setLoading(false); }
  };

  const fetchAiExplanation = async (data) => {
    setAiLoading(true);
    try {
      const { data: aiData } = await api.post('/ai/explain', {
        type: 'cost', data: {
          project_type: form.project_type, size: form.size_sqm, finish_level: form.finish_level,
          location: LOCATIONS.find(l => l.value === form.location)?.label || form.location,
          construction_cost: (data.construction_cost.min + data.construction_cost.max) / 2,
          architecture_fees: (data.architecture_fees.min + data.architecture_fees.max) / 2,
          total_cost: (data.total_cost.min + data.total_cost.max) / 2,
        }
      });
      setAiExplanation(aiData.explanation);
    } catch { /* AI is optional — silently fall through */ }
    finally { setAiLoading(false); }
  };

  const handleSaveContact = async () => {
    if (!form.email) { toast.error('Please enter your email'); return; }
    try {
      await api.post('/cost/calculate', { ...form, size_sqm: Number(form.size_sqm) });
      toast.success('Report saved! We\'ll be in touch.');
      setShowContact(false);
      trackEvent('cost_contact_saved', { email: form.email });
    } catch { toast.error('Failed to save'); }
  };

  const handleDownloadPDF = async () => {
    if (!result) return;
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND}/api/reports/cost/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...result, ai_explanation: aiExplanation, email: form.email, name: form.name }),
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = blobUrl;
      link.setAttribute('download', 'raum-cost-report.pdf');
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 200);
      trackEvent('cost_pdf_download');
      api.post('/tools/pdf-download', { tool: 'cost', report_id: result?.report_id || '', email: form.email || '' }).catch(() => {});
      toast.success('Report downloaded');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleGateSubmit = async ({ name, email, phone }) => {
    setForm((f) => ({ ...f, name, email, phone }));
    await runCalculation({ name, email, phone });
    trackEvent('cost_lead_captured', { email });
  };

  const pieData = result ? [
    { name: 'Construction', value: result.breakdown_percentages.construction },
    { name: 'Architecture', value: result.breakdown_percentages.architecture },
    { name: 'Engineering', value: result.breakdown_percentages.engineering },
    { name: 'Approvals', value: result.breakdown_percentages.approvals },
  ] : [];

  const barData = result ? [
    { name: 'Construction', min: result.construction_cost.min, max: result.construction_cost.max },
    { name: 'Architecture', min: result.architecture_fees.min, max: result.architecture_fees.max },
    { name: 'Engineering', min: result.engineer_fees.min, max: result.engineer_fees.max },
    { name: 'Approvals', min: result.council_approval.min, max: result.council_approval.max },
  ] : [];

  return (
    <div className="min-h-screen bg-brand-light pt-20" data-testid="cost-calculator-page">
      <div className="section-dark py-12 sm:py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-brand-accent font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Interactive Tool</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-white mb-4" style={{lineHeight: 1.1}}>What Will This Really Cost?</h1>
          <p className="text-white/70 font-body text-base sm:text-base lg:text-lg max-w-2xl leading-relaxed">
            Real-world cost ranges based on comparable projects and local conditions. Architecture fees, engineering, council approvals — with no false precision.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 sm:py-12">
        <div className="bg-white rounded-sm border border-black/10 p-6 sm:p-8 mb-8" data-testid="cost-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Project Type</Label>
              <Select value={form.project_type} onValueChange={(v) => setForm(p => ({ ...p, project_type: v }))}>
                <SelectTrigger data-testid="cost-select-type" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Building Type</Label>
              <Select value={form.building_type} onValueChange={(v) => setForm(p => ({ ...p, building_type: v }))}>
                <SelectTrigger data-testid="cost-select-building-type" className="rounded-sm h-11"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{BUILDING_TYPES.flatMap(g => g.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Location *</Label>
              <Select value={form.location} onValueChange={(v) => setForm(p => ({ ...p, location: v }))}>
                <SelectTrigger data-testid="cost-select-location" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Size (m²) *</Label>
              <Input data-testid="cost-input-size" type="number" value={form.size_sqm} onChange={(e) => setForm(p => ({ ...p, size_sqm: e.target.value }))} placeholder="e.g. 250" className="rounded-sm h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Finish Level</Label>
              <Select value={form.finish_level} onValueChange={(v) => setForm(p => ({ ...p, finish_level: v }))}>
                <SelectTrigger data-testid="cost-select-finish" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{FINISH_LEVELS.map(f => <SelectItem key={f.value} value={f.value}>{f.label} ({f.desc})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="cost-calculate-btn" onClick={handleCalculate} disabled={loading} className="mt-8 bg-brand-dark text-white hover:bg-gray-800 rounded-sm h-12 px-10 font-body text-sm font-semibold uppercase tracking-wider">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing...</> : <>See the Full Picture <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>

        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="cost-results">
              {/* Total */}
              <div className="bg-brand-dark rounded-sm p-8 text-center">
                <p className="text-white/50 font-heading text-xs uppercase tracking-wider mb-2">Estimated Total Cost</p>
                <p className="font-heading text-3xl md:text-4xl font-medium text-brand-accent" data-testid="cost-total">
                  {formatZARFull(result.total_cost.min)} — {formatZARFull(result.total_cost.max)}
                </p>
                <p className="text-white/40 text-sm font-body mt-2">
                  For a {result.size_sqm}m² {result.project_type === 'renovation' ? 'renovation' : 'new build'} with {result.finish_level} finishes
                </p>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="cost-pie-chart">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60 mb-4">Cost Breakdown</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name} ${value}%`} labelLine={false}>
                        {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(value) => `${value}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="cost-bar-chart">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60 mb-4">Cost Ranges</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={barData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis type="number" tickFormatter={(v) => `R${(v / 1000).toFixed(0)}K`} fontSize={11} />
                      <YAxis type="category" dataKey="name" fontSize={11} width={90} />
                      <Tooltip formatter={(v) => formatZARFull(v)} />
                      <Bar dataKey="min" fill="#A1A1AA" name="Min" radius={[0, 2, 2, 0]} />
                      <Bar dataKey="max" fill="#CBA871" name="Max" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Detail Breakdown */}
              <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="cost-detail-breakdown">
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60 mb-4">Detailed Breakdown</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Construction', min: result.construction_cost.min, max: result.construction_cost.max, pct: result.breakdown_percentages.construction },
                    { label: 'Architecture Fees', min: result.architecture_fees.min, max: result.architecture_fees.max, pct: result.breakdown_percentages.architecture },
                    { label: 'Engineering', min: result.engineer_fees.min, max: result.engineer_fees.max, pct: result.breakdown_percentages.engineering },
                    { label: 'Council Approvals', min: result.council_approval.min, max: result.council_approval.max, pct: result.breakdown_percentages.approvals },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
                      <div>
                        <span className="font-body text-sm font-medium text-brand-dark">{item.label}</span>
                        <span className="text-brand-dark/40 text-xs ml-2">({item.pct}%)</span>
                      </div>
                      <span className="font-body text-sm font-semibold text-brand-dark">{formatZARFull(item.min)} - {formatZARFull(item.max)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between py-3 border-t-2 border-brand-accent/30">
                    <span className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark">Total</span>
                    <span className="font-heading text-lg font-semibold text-brand-accent-dark">{formatZARFull(result.total_cost.min)} - {formatZARFull(result.total_cost.max)}</span>
                  </div>
                </div>
              </div>

              {/* Cost-optimisation reassurance (mirrored from PDF) */}
              <div className="flex items-start gap-3 px-4 py-3 border-l-2 border-brand-accent/60 bg-brand-light/40 rounded-sm" data-testid="cost-optimisation-note">
                <p className="text-brand-dark/70 font-body text-sm italic leading-relaxed">
                  These figures describe a starting position. Architecture is the process that refines scope and improves outcomes.
                </p>
              </div>

              {/* AI Explanation */}
              <div className="bg-brand-light rounded-sm border border-brand-accent/15 p-6" data-testid="cost-ai-explanation">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-brand-accent" />
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60">What This Means for Your Project</h3>
                </div>
                <p className="text-brand-dark/30 text-xs font-body mb-3">Architect-guided insight</p>
                {aiLoading ? (
                  <div className="flex items-center gap-2 text-brand-dark/40 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Interpreting your results...</div>
                ) : aiExplanation ? (
                  <div className="prose prose-sm max-w-none text-brand-dark/70 font-body leading-relaxed whitespace-pre-wrap">{aiExplanation}</div>
                ) : (
                  <p className="text-brand-dark/40 text-sm font-body">Architect-guided insights will appear after your results are generated.</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                <Button data-testid="cost-pdf-btn" onClick={handleDownloadPDF} variant="outline" className="border-brand-dark text-brand-dark hover:bg-brand-dark/5 rounded-sm h-11 px-8 font-body font-semibold text-sm uppercase tracking-wider">
                  <Download className="w-4 h-4 mr-2" /> Download PDF Report
                </Button>
                <Button data-testid="cost-ask-btn" variant="outline" onClick={() => navigate('/ask-architect', { state: { context: { type: 'cost', data: { project_type: result.project_type, size_sqm: result.size_sqm, finish_level: result.finish_level, location: result.location, total_min: result.total_cost?.min, total_max: result.total_cost?.max } } } })} className="border-brand-dark/30 text-brand-dark/70 hover:bg-brand-dark/5 rounded-sm h-11 px-8 font-body font-semibold text-sm uppercase tracking-wider">
                  <MessageSquare className="w-4 h-4 mr-2" /> What to Think About Next
                </Button>
                {!showContact && (
                  <Button data-testid="cost-save-btn" onClick={() => setShowContact(true)} className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm h-11 px-8 font-body font-semibold text-sm uppercase tracking-wider">
                    Discuss with an Architect <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
              <p className="text-center text-brand-accent-dark/60 font-heading italic text-sm">
                We create space — by turning ideas into clarity.
              </p>

              {showContact && (
                <div className="bg-white rounded-sm border border-black/10 p-6">
                  <h3 className="font-heading text-lg font-medium mb-4">Save Your Report</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input data-testid="cost-contact-name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Name" className="rounded-sm h-10" />
                    <Input data-testid="cost-contact-email" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email *" className="rounded-sm h-10" required />
                    <Input data-testid="cost-contact-phone" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" className="rounded-sm h-10" />
                  </div>
                  <Button data-testid="cost-contact-submit" onClick={handleSaveContact} className="mt-4 bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-sm font-semibold uppercase tracking-wider">
                    Save & Send Report
                  </Button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ReportLeadGate
        open={showGate}
        onClose={() => setShowGate(false)}
        onSubmit={handleGateSubmit}
        toolLabel="Cost Report"
        initial={{ name: form.name, email: form.email, phone: form.phone }}
      />
    </div>
  );
}
